import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  ArrowLeft, Camera, UploadSimple, Spinner, X, Plus, ArrowRight,
  Storefront, CheckCircle, Warning, ListChecks, Trash,
} from "@phosphor-icons/react";
import EbayListingModal from "@/components/app/EbayListingModal";
import { useConfirm } from "@/components/app/ConfirmDialog";

/**
 * Bulk upload wizard:
 *   1. User uploads photos for "Item N"
 *   2. Tap "Next Item" → background AI scan + match-to-history → Item N+1 capture
 *   3. Tap "Start Listing" → walks the queue, opens eBay listing modal per item
 *      sequentially (waits for each item's verdict to be ready before showing it)
 */
export default function BulkUploadPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [sessionId, setSessionId] = useState(null);
  const [currentItem, setCurrentItem] = useState(null); // { sku, num, photos: [] }
  const [items, setItems] = useState([]); // all items in session (for nav)
  const [resumable, setResumable] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("intro"); // intro | capture | listing
  const fileRef = useRef(null);

  const refreshItems = useCallback(async (sid) => {
    if (!sid) return [];
    try {
      const { data } = await api.get(`/bulk/sessions/${sid}/queue`);
      setItems(data.items || []);
      return data.items || [];
    } catch {
      return [];
    }
  }, []);

  // Resume from URL or fetch active sessions
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session");
    if (sid) {
      setSessionId(sid);
      setPhase("capture");
      refreshItems(sid).then((itemsList) => {
        // Resume at the most recently worked-on item (last one), or create new if none
        if (itemsList.length > 0) {
          const last = itemsList[itemsList.length - 1];
          const num = parseInt(last.sku.replace(/[^0-9]/g, ""), 10) || 1;
          setCurrentItem({
            sku: last.sku,
            num,
            photos: (last.photos || []).map((p) => ({ id: p.id, storage_path: p.storage_path })),
          });
        } else {
          createNewItem(sid).catch(() => {});
        }
      });
    } else {
      api.get("/bulk/sessions").then(({ data }) => {
        const active = (data || []).filter((s) => s.photos_total > 0).slice(0, 5);
        setResumable(active);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNewSession = async () => {
    setBusy(true);
    setError("");
    try {
      const { data: session } = await api.post("/bulk/sessions");
      setSessionId(session.id);
      setPhase("capture");
      await createNewItem(session.id);
      const url = new URL(window.location.href);
      url.searchParams.set("session", session.id);
      window.history.replaceState({}, "", url.toString());
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to start session");
    } finally {
      setBusy(false);
    }
  };

  const createNewItem = async (sid) => {
    const { data } = await api.post(`/bulk/sessions/${sid}/items`);
    setCurrentItem({ sku: data.item_sku, num: data.item_num, photos: [] });
    await refreshItems(sid);
  };

  // Navigate to a specific existing item (load its photos into editor)
  const gotoItem = async (item) => {
    if (!item || !sessionId) return;
    // Fire-and-forget finalize on current item if it has photos (no-op if no changes)
    if (currentItem && currentItem.photos.length > 0 && currentItem.sku !== item.sku) {
      api.post(`/bulk/sessions/${sessionId}/items/${currentItem.sku}/finalize`).catch(() => {});
    }
    const num = parseInt(item.sku.replace(/[^0-9]/g, ""), 10) || 1;
    setCurrentItem({
      sku: item.sku,
      num,
      photos: (item.photos || []).map((p) => ({ id: p.id, storage_path: p.storage_path })),
    });
  };

  const handleResume = (sid) => {
    const url = new URL(window.location.href);
    url.searchParams.set("session", sid);
    window.location.href = url.toString();
  };

  const handleDeleteSession = async (sid, e) => {
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete this session?",
      message: "All its photos will be removed. Verdicts already matched will stay in your history.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/bulk/sessions/${sid}`);
      setResumable((prev) => prev.filter((s) => s.id !== sid));
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to delete session");
    }
  };

  const handlePhotoSelect = async (e) => {
    if (!sessionId || !currentItem) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    setError("");

    // Upload one at a time on production. iPhone photos can hit ~37MB peak in
    // PIL during decode; running 2-3 in parallel was OOM-killing the K8s pod
    // (single worker, ~512MB limit). Sequential adds latency but eliminates
    // crash-restart loops. Backend compresses to <1MB so total time is similar.
    const CONCURRENCY = 1;
    const failed = [];
    // Adaptive throttle: when consecutive failures hit this threshold the pool
    // pauses for a longer cooldown — handles upstream storage rate-limits
    // (which manifest as 3-photos-then-fail patterns).
    let consecutiveFails = 0;

    const uploadOne = async (file) => {
      // Per-file retries with progressive backoff. Final wait is long enough
      // (~30s) to clear most per-minute rate-limit windows.
      const RETRY_DELAYS = [1500, 5000, 30000]; // 1.5s, 5s, 30s — total ~37s
      let lastErr = null;
      for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
        // Adaptive cooldown: if the LAST few files all failed, wait extra
        // before this attempt so we don't hammer the rate-limit.
        if (attempt === 0 && consecutiveFails >= 3) {
          await new Promise((r) => setTimeout(r, 15000));
        }
        try {
          const fd = new FormData();
          fd.append("file", file);
          const { data } = await api.post(
            `/bulk/sessions/${sessionId}/items/${currentItem.sku}/photo`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
          );
          setCurrentItem((prev) => prev ? ({
            ...prev,
            photos: [...prev.photos, { id: data.id, storage_path: data.storage_path }],
          }) : prev);
          consecutiveFails = 0;
          return;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const isTransient = !status || status === 0 || status === 429 ||
                              status >= 500;
          if (attempt < RETRY_DELAYS.length && isTransient) {
            await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
            continue;
          }
          break;
        }
      }
      if (lastErr) {
        consecutiveFails++;
        failed.push(file.name);
      }
    };

    // Pool with bounded concurrency
    let cursor = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, files.length) }, async () => {
      while (cursor < files.length) {
        const idx = cursor++;
        await uploadOne(files[idx]);
      }
    });
    await Promise.all(workers);

    if (failed.length) {
      const detail = failed.length === 1
        ? `Upload failed for ${failed[0]} after multiple retries. Tap + to try again — storage may be busy.`
        : `Upload failed for ${failed.length} photo(s) after multiple retries. Tap + to try again — storage may be busy.`;
      setError(detail);
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = async (photoId) => {
    if (!sessionId || !currentItem) return;
    try {
      await api.delete(`/bulk/sessions/${sessionId}/items/${currentItem.sku}/photo/${photoId}`);
      setCurrentItem((prev) => ({ ...prev, photos: prev.photos.filter((p) => p.id !== photoId) }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Remove failed");
    }
  };

  const handleNextItem = async () => {
    if (!sessionId || !currentItem || busy) return;
    setError("");

    // Find current position in items list
    const currentIdx = items.findIndex((i) => i.sku === currentItem.sku);
    const nextExisting = currentIdx >= 0 && currentIdx < items.length - 1 ? items[currentIdx + 1] : null;

    if (nextExisting) {
      // Fire-and-forget finalize on current item (backend is idempotent)
      if (currentItem.photos.length > 0) {
        api.post(`/bulk/sessions/${sessionId}/items/${currentItem.sku}/finalize`).catch(() => {});
      }
      await gotoItem(nextExisting);
      return;
    }

    // On last item (or no existing) — finalize + create new (requires photos)
    if (currentItem.photos.length === 0) return;
    const currentSku = currentItem.sku;
    api.post(`/bulk/sessions/${sessionId}/items/${currentSku}/finalize`).catch((err) => {
      console.error(`Finalize failed for ${currentSku}:`, err);
    });
    try {
      await createNewItem(sessionId);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to start next item");
    }
  };

  const handlePrevItem = async () => {
    if (!sessionId || !currentItem || busy) return;
    const currentIdx = items.findIndex((i) => i.sku === currentItem.sku);
    if (currentIdx <= 0) return;
    const prev = items[currentIdx - 1];
    await gotoItem(prev);
  };

  const handleStartListing = async () => {
    if (!sessionId || busy) return;
    setBusy(true);
    setError("");
    try {
      // Finalize the current item too if it has photos
      if (currentItem && currentItem.photos.length > 0) {
        await api.post(`/bulk/sessions/${sessionId}/items/${currentItem.sku}/finalize`);
      }
      setPhase("listing");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to start listing");
    } finally {
      setBusy(false);
    }
  };

  // ---------- RENDER ----------

  if (phase === "listing") {
    return (
      <ListingQueue
        sessionId={sessionId}
        onExit={() => setPhase("capture")}
        onAllDone={() => navigate("/scan")}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-28">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b-4 border-black z-30">
        <div className="px-4 py-3 flex items-center gap-3">
          <Link to="/scan" className="p-1.5 hover:bg-neutral-100" data-testid="back-to-scan-btn">
            <ArrowLeft size={20} weight="bold" />
          </Link>
          <h1 className="font-heading text-xl font-black uppercase tracking-tight">Bulk Upload</h1>
        </div>
      </div>

      {/* Intro: no session yet */}
      {phase === "intro" && (
        <div className="px-4 py-8">
          {resumable.length > 0 && (
            <div className="bg-white border-4 border-black p-4 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] mb-6" data-testid="resume-picker">
              <div className="font-heading text-sm font-bold uppercase tracking-wide mb-2">Resume Session</div>
              <div className="space-y-2">
                {resumable.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-stretch border-2 border-black hover:bg-neutral-50"
                  >
                    <button
                      onClick={() => handleResume(s.id)}
                      className="flex-1 text-left flex items-center justify-between p-2 active:translate-y-0.5"
                      data-testid={`resume-session-${s.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-heading text-xs font-bold uppercase">
                          {s.items_count || 0} item{(s.items_count || 0) !== 1 ? "s" : ""} · {s.photos_total} photo{s.photos_total !== 1 ? "s" : ""}
                        </div>
                        <div className="font-body text-[11px] text-neutral-500 truncate">
                          {new Date(s.created_at).toLocaleString()}
                        </div>
                      </div>
                      <span className="font-heading text-[10px] font-bold uppercase bg-black text-white px-2 py-1">Resume</span>
                    </button>
                    <button
                      onClick={(e) => handleDeleteSession(s.id, e)}
                      className="px-3 border-l-2 border-black hover:bg-red-100 active:bg-red-200"
                      title="Delete session"
                      data-testid={`delete-session-${s.id}`}
                    >
                      <Trash size={16} weight="bold" className="text-red-600" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="font-body text-[10px] text-neutral-500 text-center mt-3 uppercase tracking-wide">— or start fresh —</div>
            </div>
          )}

          <div className="bg-white border-4 border-black p-6 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
            <div className="text-center mb-4">
              <Camera size={40} weight="bold" className="mx-auto text-black mb-2" />
              <h2 className="font-heading text-xl font-black uppercase tracking-tight">Bulk Upload Wizard</h2>
              <p className="font-body text-sm text-neutral-600 mt-1">
                Upload photos one item at a time. We'll match &amp; price each one in the background while you keep going.
              </p>
            </div>
            <button
              onClick={startNewSession}
              disabled={busy}
              className="w-full bg-black text-white py-4 font-heading text-lg font-bold uppercase tracking-widest border-4 border-black shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all flex items-center justify-center gap-3 disabled:opacity-50"
              data-testid="start-bulk-btn"
            >
              {busy ? <Spinner size={22} weight="bold" className="animate-spin" /> : <Plus size={22} weight="bold" />}
              Start
            </button>
          </div>
        </div>
      )}

      {/* Capture phase */}
      {phase === "capture" && currentItem && (
        <div className="px-4 py-6">
          <div className="bg-white border-4 border-black shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
            <div className="border-b-4 border-black px-3 py-3 flex items-center gap-2">
              <button
                onClick={handlePrevItem}
                disabled={items.findIndex((i) => i.sku === currentItem.sku) <= 0 || busy}
                className="p-2 border-2 border-black active:translate-y-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="prev-item-btn"
                title="Previous item"
              >
                <ArrowLeft size={16} weight="bold" />
              </button>
              <div className="flex-1 min-w-0 text-center">
                <div className="font-heading text-lg font-black uppercase tracking-tight leading-tight" data-testid="current-item-label">
                  Item {currentItem.num}
                </div>
                {items.length > 0 && (
                  <div className="font-body text-[10px] text-neutral-500 uppercase tracking-wide">
                    {(() => {
                      const idx = items.findIndex((i) => i.sku === currentItem.sku);
                      if (idx >= 0) return `${idx + 1} of ${items.length}`;
                      return `new · ${items.length} so far`;
                    })()}
                  </div>
                )}
              </div>
              <button
                onClick={handleNextItem}
                disabled={busy || (() => {
                  const idx = items.findIndex((i) => i.sku === currentItem.sku);
                  const onLast = idx === items.length - 1 || idx < 0;
                  // Disabled on last item with no photos (nothing to finalize)
                  return onLast && currentItem.photos.length === 0;
                })()}
                className="p-2 border-2 border-black active:translate-y-0.5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="next-item-header-btn"
                title="Next item"
              >
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>

            <div className="p-4">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-file-input"
              />

              {currentItem.photos.length === 0 ? (
                <div className="py-8 text-center">
                  <Camera size={48} weight="bold" className="mx-auto text-neutral-300 mb-3" />
                  <p className="font-body text-sm text-neutral-500 mb-4">Add photos of this item.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {currentItem.photos.map((p) => (
                    <div key={p.id} className="relative aspect-square bg-neutral-100 border-2 border-black overflow-hidden group">
                      <img
                        src={`/api/images/file/${p.storage_path}`}
                        alt=""
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                      <button
                        onClick={() => removePhoto(p.id)}
                        className="absolute top-1 right-1 bg-black/80 text-white p-1 hover:bg-red-600"
                        data-testid={`remove-photo-${p.id}`}
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full bg-white border-2 border-black py-3 font-heading text-sm font-bold uppercase tracking-wide flex items-center justify-center gap-2 active:bg-neutral-50 disabled:opacity-50"
                data-testid="add-photos-btn"
              >
                {busy ? (
                  <><Spinner size={16} weight="bold" className="animate-spin" /> Uploading...</>
                ) : (
                  <><UploadSimple size={16} weight="bold" /> {currentItem.photos.length === 0 ? "Add Photos" : "Add More"}</>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-3 bg-red-50 border-2 border-red-400 p-3 font-body text-xs text-red-800" data-testid="capture-error">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Bottom action bar — Next Item / Start Listing */}
      {phase === "capture" && currentItem && (() => {
        const idx = items.findIndex((i) => i.sku === currentItem.sku);
        const onLast = idx === items.length - 1 || idx < 0;
        const nextLabel = onLast ? "Next Item" : `Go to Item ${idx + 2}`;
        const nextDisabled = busy || (onLast && currentItem.photos.length === 0);
        return (
          <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-4 border-black p-3 pb-safe grid grid-cols-2 gap-2">
            <button
              onClick={handleNextItem}
              disabled={nextDisabled}
              className="bg-black text-white py-3 font-heading text-sm font-bold uppercase tracking-widest border-2 border-black shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] hover:translate-x-[1.5px] hover:translate-y-[1.5px] hover:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="next-item-btn"
            >
              {onLast ? <Plus size={16} weight="bold" /> : <ArrowRight size={16} weight="bold" />}
              {nextLabel}
            </button>
            <button
              onClick={handleStartListing}
              disabled={busy}
              className="bg-[#00E676] text-black py-3 font-heading text-sm font-bold uppercase tracking-widest border-2 border-black shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] hover:translate-x-[1.5px] hover:translate-y-[1.5px] hover:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="start-listing-btn"
            >
              <Storefront size={16} weight="bold" /> Start Listing
            </button>
          </div>
        );
      })()}
    </div>
  );
}

/* ============================================================
 * ListingQueue — sequential eBay listing with background-await
 * ============================================================ */
function ListingQueue({ sessionId, onExit, onAllDone }) {
  const [queue, setQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [done, setDone] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchQueue = useCallback(async () => {
    const { data } = await api.get(`/bulk/sessions/${sessionId}/queue`);
    setQueue(data.items || []);
    return data.items || [];
  }, [sessionId]);

  useEffect(() => {
    fetchQueue().then((items) => {
      // Skip already-listed items at the start
      let i = 0;
      while (i < items.length && items[i].status === "listed") i++;
      setCurrentIdx(i);
      if (i >= items.length) setDone(true);
      setInitialLoading(false);
    }).catch(() => setInitialLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchQueue]);

  // When all items complete, archive the session so it drops out of the resume list
  useEffect(() => {
    if (!done || !sessionId) return;
    api.post(`/bulk/sessions/${sessionId}/archive`).catch(() => {
      // silent — user can still manually delete if needed
    });
  }, [done, sessionId]);

  // Poll while current item isn't ready — faster initial poll for snappier UX
  useEffect(() => {
    if (done || initialLoading) return;
    const item = queue[currentIdx];
    if (!item) return;
    if (item.status === "ready" || item.status === "error" || item.status === "listed") {
      setWaiting(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    setWaiting(true);
    // Poll every 1s — these calls are tiny and the AI typically finishes in 10-20s
    pollRef.current = setInterval(fetchQueue, 1000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [currentIdx, queue, done, initialLoading, fetchQueue]);

  const advance = useCallback(() => {
    setCurrentIdx((prev) => {
      const next = prev + 1;
      if (next >= queue.length) {
        setDone(true);
        return prev;
      }
      return next;
    });
  }, [queue.length]);

  const handleSuccess = async () => {
    await fetchQueue();
    advance();
  };

  const handleSkip = () => advance();

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6" data-testid="queue-loading">
        <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] text-center max-w-sm w-full">
          <Spinner size={40} weight="bold" className="animate-spin mx-auto text-black mb-4" />
          <div className="font-heading text-base font-black uppercase">Preparing Listing Queue…</div>
          <div className="font-body text-xs text-neutral-600 mt-2">
            Loading your items and scan status.
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    const listedCount = queue.filter((i) => i.status === "listed").length;
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6" data-testid="all-listed-confirmation">
        <div className="bg-white border-4 border-black w-full max-w-md p-8 shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] text-center">
          <div className="bg-[#00E676]/30 border-4 border-[#00E676] p-6 mb-5">
            <CheckCircle size={56} weight="bold" className="mx-auto text-green-700" />
          </div>
          <h2 className="font-heading text-2xl font-black uppercase">All items in the queue have been listed to eBay!</h2>
          <div className="font-body text-sm text-neutral-700 mt-3">
            {listedCount} of {queue.length} items listed.
          </div>
          <button
            onClick={onAllDone}
            className="mt-6 w-full bg-black text-white py-3 font-heading text-sm font-bold uppercase tracking-widest border-2 border-black"
            data-testid="finish-bulk-btn"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  const current = queue[currentIdx];

  return (
    <>
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[10001] bg-black text-white px-4 py-2 flex items-center gap-3 border-b-2 border-[#00E676]" data-testid="queue-progress">
        <ListChecks size={18} weight="bold" className="text-[#00E676] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-heading text-xs font-bold uppercase tracking-wide truncate">
            Item {currentIdx + 1} of {queue.length}
          </div>
          <div className="h-1 bg-neutral-700 mt-1 overflow-hidden rounded">
            <div className="h-full bg-[#00E676] transition-all" style={{ width: `${queue.length > 0 ? (currentIdx / queue.length) * 100 : 0}%` }} />
          </div>
        </div>
        <button onClick={handleSkip} className="font-heading text-[10px] font-bold uppercase px-2 py-1 bg-neutral-800 hover:bg-neutral-700" data-testid="queue-skip-btn">
          Skip <ArrowRight size={12} weight="bold" className="inline" />
        </button>
        <button onClick={onExit} className="font-heading text-[10px] font-bold uppercase px-2 py-1 bg-red-600 hover:bg-red-700" data-testid="queue-exit-btn">
          Exit
        </button>
      </div>

      {/* Waiting state */}
      {waiting && current && (
        <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6" data-testid="queue-waiting">
          <div className="bg-white border-4 border-black p-8 shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] text-center max-w-sm">
            <Spinner size={40} weight="bold" className="animate-spin mx-auto text-black mb-3" />
            <div className="font-heading text-base font-black uppercase">Scanning next item…</div>
            <div className="font-body text-xs text-neutral-600 mt-1">
              AI is analyzing photos and matching to your history. This usually takes 10-20 seconds.
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {!waiting && current && current.status === "error" && (
        <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center p-6">
          <div className="bg-white border-4 border-red-400 p-6 max-w-sm w-full text-center" data-testid="queue-item-error">
            <Warning size={40} weight="bold" className="mx-auto text-red-500 mb-3" />
            <div className="font-heading text-base font-black uppercase">Scan failed</div>
            <div className="font-body text-xs text-neutral-600 mt-2 break-words">{current.match_error || "Unknown error"}</div>
            <button
              onClick={async () => {
                try {
                  await api.post(`/bulk/sessions/${sessionId}/items/${current.sku}/retry`);
                  await fetchQueue();
                } catch (err) {
                  alert(err?.response?.data?.detail || "Retry failed — please try again");
                }
              }}
              className="mt-4 w-full bg-black text-white py-2.5 font-heading text-sm font-bold uppercase"
              data-testid="queue-retry-btn"
            >
              Retry Scan
            </button>
            <button onClick={advance} className="mt-2 w-full bg-neutral-200 text-black py-2 font-heading text-xs font-bold uppercase">
              Skip &amp; Continue
            </button>
          </div>
        </div>
      )}

      {/* Ready: show eBay listing modal for this verdict */}
      {!waiting && current && current.status === "ready" && current.verdict && (
        <EbayListingModal
          key={current.sku}
          verdict={current.verdict}
          onClose={handleSkip}
          onSuccess={handleSuccess}
        />
      )}

      {/* Already listed (e.g. resumed session) → auto-advance */}
      {!waiting && current && current.status === "listed" && (
        <AutoAdvance onAdvance={advance} />
      )}
    </>
  );
}

function AutoAdvance({ onAdvance }) {
  useEffect(() => {
    const t = setTimeout(onAdvance, 50);
    return () => clearTimeout(t);
  }, [onAdvance]);
  return null;
}
